// =============================================================================
// FILE: packages/renderer/src/components/launcher/launcherRegistry.ts
//
// ARCHITECTURAL RULE (enforced by code review):
//   The Cyberpunk Launcher is the SINGLE operational command surface of
//   BIM OPS. Every system-level tool (Quick Ingest, Project File Explorer,
//   future AI Co-Pilot, B6 Agent workflows, FRIS, etc.) MUST register
//   itself here. No module may introduce a primary UI entry point
//   outside this registry — that includes top-bar buttons, floating
//   FABs, or sidebar shortcuts. Module pages (DashboardPage, ProjectsPage…)
//   remain reachable through the Sidebar; those are navigation, not
//   command actions.
//
// CONTRACT
//   • Every LauncherCommand declares its layer (`service`), the
//     dependency gates it requires (string array), and its current
//     `availability` flag. The Launcher renders a coloured status pill
//     so users can see at a glance what's ready vs. coming soon.
//   • Status field is intentionally simple (ready | coming_soon | gated).
//     FRIS will plug into this field directly once it ships — until
//     then, modules self-declare.
// =============================================================================

import type { LucideIcon } from 'lucide-react';
import {
  Wand2, FolderTree, Bot, CalendarClock, Mail, Settings,
  Inbox, ListChecks, Activity, Layers,
} from 'lucide-react';

export type LauncherCategory =
  | 'capture' | 'navigate' | 'agents' | 'system';

export type LauncherStatus = 'ready' | 'coming_soon' | 'gated';

export interface LauncherCommand {
  id:           string;            // stable, kebab-case
  label:        string;
  shortLabel?:  string;            // for compact dock chips
  description:  string;
  icon:         LucideIcon;
  category:     LauncherCategory;
  status:       LauncherStatus;
  /** Engine / service layer this command belongs to. Helps the launcher
   *  group commands by domain. */
  service:      string;
  /** Dependency gates this command requires to be 'ready'. Listed so
   *  FRIS (when it lands) can flip status accordingly without a code
   *  change here. */
  gates:        string[];
  shortcut?:    string;            // "Ctrl+Shift+V"
  /** When set, clicking the command runs this and closes the launcher
   *  (e.g. open a dialog or toggle a drawer). */
  action?:      () => void;
  /** When set instead of `action`, the launcher routes via the existing
   *  sidebar navigator. Used for module pages. */
  routeTo?:     string;
}

export const LAUNCHER_REGISTRY: ReadonlyArray<Omit<LauncherCommand, 'action'>> = [
  // --- Capture surface ----------------------------------------------------
  {
    id: 'quick-ingest',
    label: 'Quick Ingest',
    shortLabel: 'Ingest',
    description: 'Paste anything — email, signature, meeting brief — and review extracted entities before importing.',
    icon: Wand2, category: 'capture', status: 'ready',
    service: 'SmartIngestionEngine',
    gates: [], shortcut: 'Ctrl+Shift+V',
  },
  {
    id: 'sie-inbox',
    label: 'Ingestion Inbox',
    shortLabel: 'Inbox',
    description: 'Review saved ingestion drafts queued for approval.',
    icon: Inbox, category: 'capture', status: 'coming_soon',
    service: 'SmartIngestionEngine',
    gates: [],
  },

  // --- Navigate surface ---------------------------------------------------
  {
    id: 'project-files',
    label: 'Project Files',
    shortLabel: 'Files',
    description: 'Open the dockable file tree for the active project. Inline PDF / image / text preview.',
    icon: FolderTree, category: 'navigate', status: 'ready',
    service: 'ProjectFileTreeService',
    gates: ['FileIntegrityEngine'], shortcut: 'Ctrl+\\',
  },
  {
    id: 'calendar-jump',
    label: 'Schedule',
    description: 'Month / Week / Day calendar across all projects.',
    icon: CalendarClock, category: 'navigate', status: 'ready',
    service: 'CalendarEngine', gates: [], routeTo: 'schedule',
  },

  // --- Agents -------------------------------------------------------------
  {
    id: 'ai-copilot',
    label: 'AI Co-Pilot',
    description: 'Ask BIM OPS anything about the open project. Streams answers from the universal LLM.',
    icon: Bot, category: 'agents', status: 'coming_soon',
    service: 'AICoPilotEngine', gates: ['EmergentLLMKey'],
  },
  {
    id: 'b6-agents',
    label: 'B6 Agent Workflows',
    description: 'Multi-step agents that draft follow-ups, schedule meetings, and generate deliverables.',
    icon: Layers, category: 'agents', status: 'coming_soon',
    service: 'AgentRuntime', gates: ['AICoPilotEngine'],
  },
  {
    id: 'meeting-intelligence',
    label: 'Meeting Intelligence',
    description: 'Open the rule-based extractor for the last meeting.',
    icon: ListChecks, category: 'agents', status: 'ready',
    service: 'MeetingIntelligenceEngine', gates: [],
  },

  // --- System -------------------------------------------------------------
  {
    id: 'comms',
    label: 'Comms (Email)',
    description: 'Email engine — drafts, follow-ups, threads. Live integration TBD.',
    icon: Mail, category: 'system', status: 'coming_soon',
    service: 'EmailEngine', gates: ['MicrosoftGraph'], routeTo: 'comms',
  },
  {
    id: 'fris',
    label: 'Feature Readiness',
    description: 'Live status of every engine and dependency.',
    icon: Activity, category: 'system', status: 'coming_soon',
    service: 'FRIS', gates: [],
  },
  {
    id: 'settings',
    label: 'Settings',
    description: 'App preferences, identity, sync, backup.',
    icon: Settings, category: 'system', status: 'ready',
    service: 'SettingsService', gates: [], routeTo: 'settings',
  },
];

export const CATEGORY_META: Record<LauncherCategory, { label: string; order: number }> = {
  capture:  { label: 'Capture',  order: 0 },
  navigate: { label: 'Navigate', order: 1 },
  agents:   { label: 'Agents',   order: 2 },
  system:   { label: 'System',   order: 3 },
};
